// Copyright (c) 2020-2023 Visual Intelligence LP. All rights reserved.
//
import { observable, action, computed, toJS, makeObservable, runInAction } from 'mobx';
import flatten from 'lodash/flatten';

import container from '@core/di';
import BaseStore from '@core/stores/base';
import AuthStore from '@core/stores/auth';
import Order, { OrderDTO } from '@shared/models/order';
import Asset, { AssetDTO } from '@shared/models/asset';
import AssetsStore from '@shared/stores/assets';
import DeliverablesStore from '@shared/stores/deliverables';
import Deliverable, { DeliverableDTO } from '@shared/models/deliverable';
import { DeliverableStatus } from '@shared/constants/deliverable';
import OrdersService from '@shared/services/orders';
import { showErrors } from '@shared/utils/form';
import OrderForm from '@modules/Private/submodules/Orders/forms/order';
import { StepNumber, FIRST_STEP } from '@modules/Private/submodules/Orders/pages/OrderCreating';
import { showNotification, NotificationType } from '@shared/components/Notification';
import JobRequest, { JobRequestDTO } from '@shared/models/job-request';
import { JobRequestStatus } from '@shared/constants/job-request';
import { resetFieldOption } from '@shared/components/Autocomplete';
import { ROLES } from '@shared/constants/user';
import { Id } from '@shared/types/common';
import { ListRequestParams } from '@shared/types/services';
import { OrderTask } from '@shared/models/ordertask';

export default class OrdersStore extends BaseStore<OrderDTO, Order, OrdersService> {
  static diToken = Symbol('orders-store');
  @observable _createOrderStep: StepNumber = FIRST_STEP;
  @observable _orderDetailsForm;
  @observable _assets: Array<Asset> = [];
  @observable _deliverables: { [assetId: string]: Array<Deliverable> } = {};  // dict of delis. Key is assetID. Value is list of delis corresponding to the asset.
  @observable _jobRequests: { [assetId: string]: Array<JobRequest> } = {};    // dict of jobReq. Key is assetID.  Value is list of JobReqs.
  @observable dimensionName2Id: Map<string, number> = new Map<string, number>();  // Lookup dimension ID from dimension name.

  private assetsStore = container.get<AssetsStore>(AssetsStore.diToken);
  private deliverablesStore = container.get<DeliverablesStore>(DeliverablesStore.diToken);
  private authStore = container.get<AuthStore>(AuthStore.diToken);
  
  private taskDict: {}; // Dictionary of tasks applicable to this order

  constructor() {
    super({ service: container.get<OrdersService>(OrdersService.diToken) });

    // Get dict to lookup dimension ID from dimension name.	
    this.deliverablesStore.getDimensionName2Id().then(d => this.dimensionName2Id = d);
    this._orderDetailsForm = undefined;

    makeObservable(this);
  }

  // Make sure id is defined.
  private normalizeDimensionId(dimName: string): number
  {
    if(this.dimensionName2Id === undefined) return -1;
    const id = this.dimensionName2Id.get(dimName);
    return id === undefined ? -1 : id;
  }

  // Get an order given an Id.  Return limited info if role is customerUser.
  @action getById = async (id: Id) => {
    this.loading.item = true;

    try {
      const order = this.authStore.user.roles.includes(ROLES.customerUser)
        ? await this.service.getOrderShortInfo(id)
        : await this.service.getOrderFullInfo(id);

      this.taskDict = Object.assign({}, ...order.orderTasks.map((x) => ({[x.delTypeId]: x.isInternal})));
      this.loading.item = false;
      return order;
    } catch (err) {
      this.loading.item = false;
      throw err;
    }
  }

  @action getAssetList = async (orderId: Id) => {
    return await this.service.getAssetList(orderId);
  }


  @action initializeForm = () => {
    if (!this._orderDetailsForm) {
      runInAction(() => {
        this._orderDetailsForm = new OrderForm();
      });
    }
  }

  @computed get assets() {
    return this._assets;
  }

  @computed get deliverables() {
    return this._deliverables;
  }

  @computed get allDeliverables() {
    return flatten(Object.values(toJS(this._deliverables)));
  }

  @computed get jobRequests() {
    return this._jobRequests;
  }

  @computed get orderDetailsForm() {
    return this._orderDetailsForm;
  }

  @computed get isValidOrderDetailsStep() {
    return this._orderDetailsForm?.isValid;
  }

  @computed get hasUnsavedChanges() {
    const formData = this._orderDetailsForm.values();

    return Object.keys(formData).some((key) => formData[key]);
  }

  @computed get canCreateOrder() {
    const hasAsset = Boolean(this._assets.length);
    const everyAssetHasDeliverable = this._assets.every(({ id }) => this._deliverables[id]?.length);

    return hasAsset && everyAssetHasDeliverable;
  }

  @computed get createOrderStep() {
    return this._createOrderStep;
  }

  createJobRequest = (
    orderId: OrderDTO['id'],
    assetId: AssetDTO['id'],
    data: JobRequestDTO & { deliverableIdentifiers: Array<DeliverableDTO['id']> }
  ) => {
    return this.service.createJobRequest(orderId, assetId, data);
  }

  editJobRequest = (
    jobRequestId: JobRequestDTO['id'],
    orderId: OrderDTO['id'],
    assetId: AssetDTO['id'],
    data: Partial <JobRequestDTO & { deliverableIdentifiers: Array<DeliverableDTO['id']>}>
  ) => {
    return this.service.editJobRequest(jobRequestId, orderId, assetId, data);
  }

  validateDeliverablesDate = (
    assetId: AssetDTO['id'],
    data: JobRequestDTO & { deliverableIdentifiers: Array<DeliverableDTO['id']> }
  ) => {
    const assetDeliverables = this._deliverables[assetId];
    const selectedDeliverables = data.deliverableIdentifiers.map((id: DeliverableDTO['id']) => {
      return assetDeliverables.find((deliverable) => deliverable.id == id);
    });
    const jobRequestDueDate = data.dueDate;
    const overdueDeliverables = selectedDeliverables.filter((deliverable: Deliverable) => {
      return new Date(deliverable.deadline).getTime() > new Date(jobRequestDueDate).getTime();
    });

    if (overdueDeliverables.length) {
      const overdueDeliverablesLabels = overdueDeliverables
        .map(({ deliverableTypeName }: Deliverable) => deliverableTypeName)
        .join(', ');

      showNotification(`${overdueDeliverablesLabels} due date should be earlier than job request due date`, NotificationType.error);

      throw new Error('Wrong deliverables date');
    }
  }

  canEditJobRequest = (jobRequest: JobRequest) => {
    const restrictedJobRequestStatuses = [JobRequestStatus.completed, JobRequestStatus.completedWithFailures, JobRequestStatus.failed];
    const hasRestrictedStatus = restrictedJobRequestStatuses.includes(jobRequest.status);

    if (jobRequest.isBlocked || hasRestrictedStatus) {
      return false;
    }
    
    const restricted2DDeliverableStatuses = 
      [DeliverableStatus.postProcessingCompleted, DeliverableStatus.postProcessingFailed];
    const restrictedOtherDeliverablesStatuses = 
      [DeliverableStatus.completed, DeliverableStatus.failed, DeliverableStatus.completedWithIssues];

    const deliverablesAreInAllowedStatuses = jobRequest.deliverables.some(({ status, dimensionId }) => {
      const conditions = {
        [this.normalizeDimensionId('Collect')]: !restricted2DDeliverableStatuses.includes(status),
        [this.normalizeDimensionId('Process')]: !restrictedOtherDeliverablesStatuses.includes(status),
      };

      return conditions[dimensionId];
    });

    return deliverablesAreInAllowedStatuses;
  }

  // Add a jobReq to an asset's jobreq list.
  @action setJobRequest = (assetId: Asset['id'], data: JobRequestDTO) => {
    const jobRequests = this._jobRequests[assetId];
    const hasJobRequest = jobRequests.find((jobRequest) => jobRequest.id === data.id);

    if (hasJobRequest) {
      return;
    }

    const instance = new JobRequest(data);

    // in setDeliverable this._deliverables[assetId].push(instance); is used citing 'more efficient'.  Why not here?
    // this._jobRequests = {
    //   ...this._jobRequests,
    //   [assetId]: [...jobRequests, instance]
    // };

    this._jobRequests[assetId].push(instance);
  }

  @action updateJobRequest = (assetId: Asset['id'], jobRequestId: JobRequestDTO['id'], data: JobRequestDTO) => {
    const matchedJobrequest = this._jobRequests[assetId].find((jobRequest) => jobRequest.id == jobRequestId);

    matchedJobrequest?.update(data);
  }

  @action removeJobRequest = (assetId: Asset['id'], jobRequestId: JobRequestDTO['id']) => {
    this._jobRequests = {
      ...this._jobRequests,
      [assetId]: this._jobRequests[assetId].filter((jobRequest) => jobRequest.id != jobRequestId)
    };

    showNotification('Job request has been successfully deleted');
  }

  @action deleteJobRequest = async (
    orderId: OrderDTO['id'],
    assetId: AssetDTO['id'],
    jobRequest: { id: JobRequestDTO['id']; status: JobRequestDTO['status'] }
  ) => {
    if (jobRequest.status != JobRequestStatus.pending) {
      showNotification('This job request is not in pending status.', NotificationType.error);

      return;
    }

    try {
      await this.service.deleteJobRequest(orderId, assetId, jobRequest.id);
      this.removeJobRequest(assetId, jobRequest.id);
    } catch (err) {
      throw err;
    }
  }

  // Call the corresponding service to submit a request to create a new order to portal-api.
  createOrder = async (orderTasks: OrderTask[]) => {
    // Create a new orderDTO from the form data.
    const formData = this._orderDetailsForm.values();
    const data: OrderDTO = {
      ...formData,
      orderTasks: orderTasks,
      assets: this._assets.map((asset) => {

        return {
          assetId: asset.id,
          deliverables: this._deliverables[asset.id].map(({ asJson }) => asJson)
        };
      })
    };

    // Call the service to submit the form.
    try {
      await this.service.create(data);
      this.getList();
      this.resetManagement();
    } 
    catch (err) {
      const fieldErrors = err?.response?.data?.errors;

      // Show errors on the form.
      if (fieldErrors) {
        this.setOrderCreationStep(StepNumber.orderDetails);

        showErrors(fieldErrors, this._orderDetailsForm);
      }

      throw err;
    }
  }

  createDeliverable = (orderId: OrderDTO['id'], assetId: AssetDTO['id'], data: Partial<DeliverableDTO>) => {
    return this.service.createDeliverable(orderId, assetId, {...data, datumId: 1, projectionId: 1});
  }

  // Get a list of delis available for a new jobRea.
  getAvailableDeliverablesForJobRequest = (assetId: AssetDTO['id']) => {
    const allDeliverables = this._deliverables[assetId];
    const selectedDeliverables = this._jobRequests[assetId].map((jobRequest) => { // Delis that have been selected in existing JobReqs.
      return jobRequest.deliverables.map((deliverable) => deliverable.deliverableTypeName);
    });
    const normalizedSelectedDeliverables = flatten(selectedDeliverables);

    // return delis that have not been selected.
    return allDeliverables.filter(({ deliverableTypeName }) => !normalizedSelectedDeliverables.includes(deliverableTypeName));
  }

  getAvailableDeliverableTypes = (assetId: AssetDTO['id'], editingDeliverableType?: DeliverableDTO['deliverableType']) => {
    const addedDeliverables = this._deliverables[assetId];

    // Editing mode: return only the target deliverable 
    // Creating mode: return a list of deliverables not added yet
    const returnedTypes = editingDeliverableType ? 
      // Editing
      this.deliverablesStore.deliverableTypes.filter((deliverable) => deliverable.id == editingDeliverableType) : 
      // Creating
      this.deliverablesStore.deliverableTypes.filter((deliverable) => {
        return addedDeliverables.every((addedDeliverable) => {
          return addedDeliverable.deliverableType != deliverable.id || deliverable.id == editingDeliverableType;
        });
      });
    
    // Return deliverables in the OrderTasks
    const filteredTypes = returnedTypes.filter(delType => this.taskDict == undefined || delType.id in this.taskDict);
    return filteredTypes;
  }

  isDeliverableLinkedWithJobRequest = (assetId: AssetDTO['id'], deliverableType: DeliverableDTO['deliverableType']) => {
    const assetJobRequests = this._jobRequests[assetId];

    return assetJobRequests.some((jobRequest) => {
      return jobRequest.deliverables.some((deliverable) => deliverable.deliverableType == deliverableType);
    });
  }

  // This check is not used in the current version.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  canEditDeliverable = (deliverableDimension: DeliverableDTO['dimensionId'], deliverableStatus: DeliverableDTO['status']) => {

    
    return true;
  }

  getAssetFromOrder = (id: number) => this._assets.find((asset) => asset.id === id);

  @action setDeliverables = (assetId: Asset['id'], deliverables: Array<DeliverableDTO>) => {
    this._deliverables = {
      ...this._deliverables,
      [assetId]: deliverables.map((data) => new Deliverable(data))
    };
  }

  // Add a deli to an asset deliverable list.
  @action setDeliverable = (assetId: Asset['id'], data: DeliverableDTO) => {
    const deliverables = this._deliverables[assetId];

    const hasDeliverable = deliverables.find((deliverable) => deliverable.id === data.id);
    // Skip if deli already in list.
    if (hasDeliverable) {
      return;
    }

    // Create a new deli obj from the deliDTO.
    const instance = new Deliverable({
      ...data,
      datumId: 1,
      projectionId: 1
    });

    // Add deli obj to list.
    this._deliverables[assetId].push(instance);
  }

  editDeliverable = (id: DeliverableDTO['id'], data: Partial<DeliverableDTO>) => {
    return this.deliverablesStore.edit(id, {...data, datumId: 1, projectionId: 1});
  }

  @action updateDeliverable = (assetId: Asset['id'], deliverableId: DeliverableDTO['id'], data: Partial<DeliverableDTO>) => {
    const matchedDeliverable = this._deliverables[assetId].find((deliverable) => deliverable.id == deliverableId);

    matchedDeliverable?.update(data);
  }

  @action removeDeliverable = (assetId: Asset['id'], deliverableId: DeliverableDTO['id']) => {
    this._deliverables = {
      ...this._deliverables,
      [assetId]: this._deliverables[assetId].filter((deliverable) => deliverable.id != deliverableId)
    };

    showNotification('Deliverable has been successfully deleted');
  }

  // Send request to delete a deliverable.
  @action deleteDeliverable = async (
    orderId: OrderDTO['id'],
    assetId: Asset['id'],
    deliverable: { id: DeliverableDTO['id']; status: DeliverableDTO['status'] }
  ) => {
    try {

      // Cannot delete completed deliverable.
      const isCompletedDeliverable = deliverable.status == DeliverableStatus.completed;
      if (isCompletedDeliverable) {
        showNotification('This deliverable is in completed status.', NotificationType.error);
        return;
      }

      // Cannot delete the only deliverable left in an asset.
      //const isLastDeliverable = this._deliverables[assetId]?.length === 1;
      //if (isLastDeliverable) {
      //  showNotification('This is the only deliverable in the asset', NotificationType.error);
      //  return;
      //}

      // const isDeliverableLinkedWithJobRequest = this._jobRequests[assetId]?.some(({ deliverables }) => {
      //   const ids = deliverables.map(({ id }) => id);
      //   return ids.some((id) => id == deliverable.id);
      // });

      // // Cannot delete deliverable belonged to a job request.
      // if (isDeliverableLinkedWithJobRequest) {
      //   showNotification('This deliverable is connected to a job request. Please delete the job request first', NotificationType.error);
      //   return;
      // }

      await this.service.deleteDeliverable(orderId, assetId, deliverable.id);

      this.removeDeliverable(assetId, deliverable.id);
    } 
    catch (err) {
      console.log("****** Exception in deleteDeliverable: ", err);
      throw err;
    }
  }

  addAssetToOrder = (orderId: OrderDTO['id'], assetId: AssetDTO['id']) => {
    const data = {
      assetId,
      deliverables: this._deliverables[assetId].map((deliverable) => deliverable.asJson),
    };

    return this.service.addAssetToOrder(orderId, data);
  }

  @action clearAssets = () => {
    this._assets = [];
    this._deliverables = {};
  }

  // Save list of assets in a class var.
  @action setAssetList = (assets) => {
    this._assets = [];
    assets.forEach((asset) => {
      // if (this.getAssetFromOrder(asset.id) == null)
      // {
        this._deliverables[asset.id] = [];
        this._jobRequests[asset.id] = [];

        // Add an asset to the local list.
        this._assets.push(new Asset({id: asset.id, type: asset.construction.type, name: asset.name}));
        // Get a handle to the local copy.
        const currentAsset = this.getAssetFromOrder(asset.id);
        // Update the local copy.
        currentAsset?.update(asset);
      // }
    });
  }
  
  // Load data from portal-api for an asset in an order.
  // Input:
  //   id: asset Id.
  //   type: asset type.
  //   name: asset name.
  @action setAsset = async (assetData: { id: AssetDTO['id']; type: AssetDTO['type']; name: AssetDTO['name'] }) => {    
    // Skip if already loaded.
    if (this.getAssetFromOrder(assetData.id)) {
      console.log('****** Asset is already in the order.');
      return;
    }

    // Init current jobRequest and deliverables.
    this._deliverables[assetData.id] = [];
    this._jobRequests[assetData.id] = [];
    // Add an asset.
    this._assets.push(new Asset(assetData));
    // Load asset data from portal-api.
    const data = await this.assetsStore.get(assetData.id, assetData.type);
    // Locate the local copy.
    const currentAsset = this.getAssetFromOrder(assetData.id);
    // Update the local copy.
    currentAsset?.update(data);
  }

  // Note: bypass query remote server for testing.
  @action setAssetFaked = async (assetData: { id: AssetDTO['id']; type: AssetDTO['type']; name: AssetDTO['name'] }) => {
    // Skip if already loaded.
    if (this.getAssetFromOrder(assetData.id)) {
      console.log('****** Asset is already in the order.');
      return;
    }

    this._deliverables[assetData.id] = [];
    this._jobRequests[assetData.id] = [];

    this._assets.push(new Asset(assetData));

    const data = {
      "id": assetData.id,
      "assetImageUrl": "https://vi-prd-images.s3.amazonaws.com/assets/1105",
      "customerName": "SAC Wireless",
      "isBlocked": false,
      "isActive": true,
      "customerId": 3,
      "name": `Schoonover ${(new Date()).getTime()}`,
      "serialNumber": "E16-1426",
      "address": {
          "state": "IL",
          "city": "Unknown",
          "zipCode": "0",
          "country": "USA",
          "addressLine": "Unknown",
          "county": "Unknown"
      },
      "coordinates": {
          "lat": 44.19743611,
          "lon": -106.27025
      },
      "note": "",
      "construction": {
          "height": 1,
          "type": 2
      }
    }

    const currentAsset = this.getAssetFromOrder(assetData.id);

    currentAsset?.update(data);
  }


  @action removeAsset = (id: Asset['id']) => {
    this._assets = this._assets.filter((asset) => String(asset.id) !== String(id));
    this._deliverables[id] = [];
    this._jobRequests[id] = [];

    showNotification('Asset has been successfully removed');
  }

  deleteAsset = async (orderId: OrderDTO['id'], assetId: AssetDTO['id']) => {
    if (this._assets?.length === 1) {
      showNotification('This is the only asset in the order', NotificationType.error);

      return;
    }

    const deliverables = this._deliverables[assetId];
    const hasStartedDeliverable = deliverables.some((deliverable) => deliverable.status != DeliverableStatus.pending);

    if (hasStartedDeliverable) {
      showNotification('You cannot remove an asset with an active deliverable', NotificationType.error);

      return;
    }

    try {
      await this.service.deleteAsset(orderId, assetId);
      this.removeAsset(assetId);
    } catch (err) {
      throw err;
    }
  }

  @action setOrderCreationStep = (step: StepNumber) => {
    this._createOrderStep = step;
  }

  @action resetManagement = () => {
    this._assets = [];
    this._deliverables = {};
    this._jobRequests = {};
    this._createOrderStep = FIRST_STEP;
    this._orderDetailsForm?.reset();

    this._orderDetailsForm?.fields.forEach((field) => {
      resetFieldOption(field);
    });
  }

  // The functions below are used in the Map components. Maybe move to a new MapStore?
  getOrderFullInfoFiltered = async (id: Id, params: ListRequestParams) => {
    this.loading.item = true;

    try {
      const order = await this.service.getOrderFullInfoFiltered(id, params);

      this.loading.item = false;

      return order;
    } catch (err) {
      this.loading.item = false;
      throw err;
    }
  }

  updateOrderSheet = async (orderId: OrderDTO['id'], op: string, data: any) => {
    try {
      const order = await this.service.updateOrderSheet(orderId, op, data);

      return order;
    } catch (err) {
      this.loading.item = false;
      throw err;
    }
  }
}